import { createApi } from '@reduxjs/toolkit/query/react'
import { axiosBaseQuery, definedParams } from '../users/api'

import type {
  PortfolioCompleteness,
  ShadowBilling,
  PropertyTenant,
  PropertyProgram,
  TouBreakdownRow,
  PropertyCompletenessRow,
  VarianceRow,
  VarianceDetail,
  VarianceDetailArgs,
  PipelineRunsPage,
  FreshnessRow,
  AlertRow,
  BaseCaseModelVO,
  RunControlOverview,
  RunRequestsPage,
  RunSchedulesVO,
  MonthArg,
  ShadowBillingArgs,
  PropertyMonthArgs,
  VarianceArgs,
  PipelineRunsArgs,
  FreshnessArgs,
  AlertsArgs,
  RunRequestsArgs,
} from './saber.types'

export const saberApi = createApi({
  reducerPath: 'saber',
  baseQuery: axiosBaseQuery({ baseUrl: '/api/v1/admin' }),
  tagTypes: ['BaseCase'],
  endpoints: (builder) => ({
    // 1. Portfolio completeness (KPI strip + property table). v001: optional
    // from/to range (both required together; takes precedence over month).
    getPortfolioCompleteness: builder.query<PortfolioCompleteness, MonthArg | void>({
      query: (args) => ({
        method: 'GET',
        url: '/saber/portfolio/completeness',
        params: definedParams({ month: args?.month, from: args?.from, to: args?.to }),
      }),
    }),
    // 2. Per-property shadow-billing time series.
    getShadowBilling: builder.query<ShadowBilling, ShadowBillingArgs>({
      query: ({ cuid, from, to, granularity }) => ({
        method: 'GET',
        url: `/saber/properties/${cuid}/shadow-billing`,
        params: definedParams({ from, to, granularity }),
      }),
    }),
    // 3. Per-property tenant allocation breakdown. v001 §6: optional from/to
    // range (both required together; takes precedence over month).
    getPropertyTenants: builder.query<PropertyTenant[], PropertyMonthArgs>({
      query: ({ cuid, month, from, to }) => ({
        method: 'GET',
        url: `/saber/properties/${cuid}/tenants`,
        params: definedParams({ month, from, to }),
      }),
    }),
    // 4. Per-property TOU/season rate breakdown. v001 §6: optional from/to.
    getPropertyTouBreakdown: builder.query<TouBreakdownRow[], PropertyMonthArgs>({
      query: ({ cuid, month, from, to }) => ({
        method: 'GET',
        url: `/saber/properties/${cuid}/tou-breakdown`,
        params: definedParams({ month, from, to }),
      }),
    }),
    // 5. Per-property meter completeness with OK/WARN/CRITICAL status.
    // v001 §6: optional from/to.
    getPropertyCompleteness: builder.query<PropertyCompletenessRow[], PropertyMonthArgs>({
      query: ({ cuid, month, from, to }) => ({
        method: 'GET',
        url: `/saber/properties/${cuid}/completeness`,
        params: definedParams({ month, from, to }),
      }),
    }),
    // 6. Variance list. v001 §6: optional from/to (filters calendar months
    // overlapping the range; takes precedence over month).
    getVariance: builder.query<VarianceRow[], VarianceArgs | void>({
      query: (args) => ({
        method: 'GET',
        url: '/saber/variance',
        params: definedParams({
          month: args?.month,
          propertyCuid: args?.propertyCuid,
          material: args?.material,
          from: args?.from,
          to: args?.to,
        }),
      }),
    }),
    // 7. Variance detail (6-bucket effects decomposition).
    getVarianceDetail: builder.query<VarianceDetail, VarianceDetailArgs>({
      query: ({ invoiceCuid, meterServiceCuid }) => ({
        method: 'GET',
        url: `/saber/variance/${invoiceCuid}`,
        params: definedParams({ meterServiceCuid }),
      }),
    }),
    // 8. Pipeline run history (paginated).
    getPipelineRuns: builder.query<PipelineRunsPage, PipelineRunsArgs | void>({
      query: (args) => ({
        method: 'GET',
        url: '/saber/pipeline-runs',
        params: definedParams({ limit: args?.limit, offset: args?.offset }),
      }),
    }),
    // 9. Data-source freshness.
    getFreshness: builder.query<FreshnessRow[], FreshnessArgs | void>({
      query: (args) => ({
        method: 'GET',
        url: '/saber/freshness',
        params: definedParams({ propertyCuid: args?.propertyCuid }),
      }),
    }),
    // 10. Alerts.
    getAlerts: builder.query<AlertRow[], AlertsArgs | void>({
      query: (args) => ({
        method: 'GET',
        url: '/saber/alerts',
        params: definedParams({ acknowledged: args?.acknowledged, severity: args?.severity }),
      }),
    }),
    // 11. Base case model (years + months unified) for one property.
    getBaseCase: builder.query<BaseCaseModelVO, string>({
      query: (propertyCuid) => ({
        method: 'GET',
        url: `/saber/base-case/${propertyCuid}`,
      }),
      providesTags: (_result, _error, propertyCuid) => [{ type: 'BaseCase', id: propertyCuid }],
    }),
    // 15. Programs associated with a property (WEB-6559) — servicing.program
    // fields (name, type, billing-on, PTO date, …) surfaced on the Base Case
    // Model screen.
    getPropertyPrograms: builder.query<PropertyProgram[], string>({
      query: (propertyCuid) => ({
        method: 'GET',
        url: `/saber/properties/${propertyCuid}/programs`,
      }),
    }),
    // 12. Run-control header gauges (running / queue depth / backlog / last tick).
    getRunControlOverview: builder.query<RunControlOverview, void>({
      query: () => ({
        method: 'GET',
        url: '/saber/run-control/overview',
      }),
    }),
    // 13. Run-request history (paginated, filterable by type/status).
    getRunRequests: builder.query<RunRequestsPage, RunRequestsArgs | void>({
      query: (args) => ({
        method: 'GET',
        url: '/saber/run-control/requests',
        params: definedParams({
          limit: args?.limit,
          offset: args?.offset,
          runType: args?.runType,
          status: args?.status,
        }),
      }),
    }),
    // 14. Run schedules.
    getRunSchedules: builder.query<RunSchedulesVO, void>({
      query: () => ({
        method: 'GET',
        url: '/saber/run-control/schedules',
      }),
    }),
  }),
})

export const {
  useGetPortfolioCompletenessQuery,
  useGetShadowBillingQuery,
  useGetPropertyTenantsQuery,
  useGetPropertyProgramsQuery,
  useGetPropertyTouBreakdownQuery,
  useGetPropertyCompletenessQuery,
  useGetVarianceQuery,
  useGetVarianceDetailQuery,
  useGetPipelineRunsQuery,
  useGetFreshnessQuery,
  useGetAlertsQuery,
  useGetBaseCaseQuery,
  useGetRunControlOverviewQuery,
  useGetRunRequestsQuery,
  useGetRunSchedulesQuery,
} = saberApi

export const saberReducer = saberApi.reducer
